// import individual pieces from libraries so that the whole library is not eagerly loaded for each page 
import {
    Grid, Table, TableCell, TableContainer, TableHead, TableRow, Paper, TableBody, Typography, Button,
    Box, Link, Tabs, Tab, MenuItem
} from '@material-ui/core'
import React, { useState, useEffect } from 'react'
import * as Yup from 'yup'
import { DebounceButton, ButtonAction } from './DebounceButton'
import { DateTime } from 'luxon'
import { connect } from 'react-redux'
import { RouteComponentProps } from 'react-router'
import {
    DeviceTwinOrHeartbeatRequestedType, ModemWithOrgSiteResponseDTO, OrganizationResponseDTO, OrganizationsClient,
    ReportedTwinDTO, ReportedTwinGatewayDTO, SiteResponseDTO, SitesClient, ModemsClient, ModemHeartbeatResponseDTO, HeartbeatResponse, HeartbeatDTO
} from '../generated/ApiClient'
import { Formik, Form, Field, FieldProps } from 'formik'
import { Checkbox, Select, TextField } from 'formik-material-ui'
import { ApplicationState } from '../store'
import * as ModemsStore from '../store/Modems'
import * as TransportStore from '../store/TransportApp'
import { GlobalAdmin, GlobalEditor, OrgSiteModemEditor, useIsUserInRoles } from '../hooks/useIsUserInRoles';
import ConnectionButton from './ModemConnectionButton'
import { FirmwareUpdate, FotaProducts } from './UpdateHelper'
import TabPanel from '../common/TabPanel'
import { Link as RouterLink, NavLink } from 'react-router-dom';
import ModemPassword from './ModemPassword'
import { actionCreators as snackbar } from '../store/AlertSnackbarStore'
import { useDispatch } from "react-redux";
import { utcToLocal } from '../common/Shared'
import { MultiLineTextField } from '../common/SharedUI'

const AboutTabValue = 0;
const ConfigurationTabValue = 1;
const ConnectedDevicesTabValue = 2;
const SubgroupsTabValue = 3;
const NotAssigned = 'Not assigned';
const FormOrganizationId = 'organizationId';
const FormSiteId = 'siteId';

/** schema for the modem properties form */
// declared this way so that we can infer a type for it
const ModemFormSchema = Yup.object().shape({
    label: Yup.string().max(50).required(),
    remoteConnectivityEnabled: Yup.bool().required(),
    notes: Yup.string().max(500),
})

type ModemFormProperties = Yup.InferType<typeof ModemFormSchema>

type ModemDetailProps =
    // this combines multiple state typings from the redux store, as this component depends on both 
    TransportStore.TransportAppState
    & ModemsStore.ModemsState
    & typeof TransportStore.actionCreators
    & typeof ModemsStore.actionCreators
    & RouteComponentProps<{ modemId: string }>

export function ModemDetail(props: ModemDetailProps) {

    const [tabValue, setTabValue] = React.useState(0) // Tab value
    const [allOrganizations, setAllOrganizations] = React.useState<OrganizationResponseDTO[]>([])
    const [allSites, setAllSites] = React.useState<SiteResponseDTO[]>([])
    const [dropDownSites, setDropDownSites] = React.useState<SiteResponseDTO[]>([])
    const [formOrganization, setFormOrg] = React.useState<OrganizationResponseDTO>() // Organization state variable for the configuration form
    const [formSite, setFormSite] = React.useState<SiteResponseDTO>() // Site state variable for the configuration form
    const [heartBeatValue, setHearbeat] = React.useState<HeartbeatDTO>() // return value for lastOnlineTime
    const [currentOrganization, setCurrentOrg] = React.useState<OrganizationResponseDTO>()
    const [currentSite, setCurrentSite] = React.useState<SiteResponseDTO>() // The site the modem is currently saved to
    const isGlobalUser = useIsUserInRoles(GlobalEditor)
    const isGlobalAdmin = useIsUserInRoles(GlobalAdmin)
    const canEditModem = useIsUserInRoles(OrgSiteModemEditor);
    const dispatch = useDispatch();

    const modem = props.modems[props.match.params.modemId];
    const simpleTwin = props.deviceTwins[props.match.params.modemId]?.simpleTwin
    const disableConnect = modem ? !isGlobalUser && !modem.remoteConnectivityEnabled : true;

    function tabHandler(event: React.ChangeEvent<{}>, newValue: number) {
        setTabValue(newValue);
    };

    function changeHandler(event: any, modem: ModemWithOrgSiteResponseDTO) {
        if (event.target.name == FormOrganizationId) {
            const orgId: string = event.target.value;
            const newOrganization = allOrganizations.find(o => o.externalId === orgId)
            setFormOrg(newOrganization);
            setFormSite(undefined);
            dropDownSitesForOrg(orgId);
        }
        else if (event.target.name == FormSiteId) {
            const siteId: string = event.target.value;
            const newSite = dropDownSites.find(s => s.externalId === siteId);
            setFormSite(newSite);
        }
    }

    function renderAboutModem(modem: ModemWithOrgSiteResponseDTO, twin: ReportedTwinDTO | undefined, heartbeat: HeartbeatDTO | undefined) {

        const modemIpSetting = Object.entries(props.transportConfig?.simulatorToRemoteIpMap ?? {})
            .find(([ip, s]) => s.type === 'Modem' && s.modemHostId === modem.modemHostId);
        return (
            <Grid container>
                <Grid item sm={10}>
                    <br></br>
                    <Typography paragraph><b>Organization:</b> {currentOrganization ? <NavLink to={`/organizations/${currentOrganization.externalId}`}>{currentOrganization.name}</NavLink> : NotAssigned}</Typography>
                    <Typography paragraph><b>Site:</b> {currentSite ? <NavLink to={`/sites/${currentSite.externalId}`}>{currentSite.name}</NavLink> : NotAssigned}</Typography>
                    <Typography paragraph><b>Modem Host ID:</b> {modem.modemHostId}</Typography>
                    <Typography paragraph><b>Label:</b> {modem.label}</Typography>
                    <Typography paragraph><b>Serial ID:</b> {twin?.serialNumber ?? ''}</Typography>
                    <Typography paragraph><b>Created Date:</b> {utcToLocal(modem.created).toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS)}</Typography>
                    <Typography paragraph><b>Last Updated:</b> {utcToLocal(modem.lastUpdated).toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS)}</Typography>
                    <Typography paragraph><b>Last Time Online:</b> {heartbeat ? utcToLocal(heartbeat.lastOnlineTime).toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS) : "Unknown"}</Typography>
                    <Typography paragraph><b>RSSI:</b> {heartbeat?.mobileSignalStrength ?? 0}%</Typography>
                    <Typography paragraph><b>SIM ICCID:</b> {twin?.iccid ?? ''}</Typography>
                    <Typography paragraph><b>IMEI:</b> {twin?.imei ?? ''}</Typography>
                    <Typography paragraph><b>IMSI:</b> {twin?.imsi ?? ''}</Typography>
                    <Typography paragraph><b>Firmware Version:</b> {twin?.appFirmware ?? ''}</Typography>
                    <FirmwareUpdate currentAppVersion={twin?.appFirmware ?? ''} fotaProduct={FotaProducts.Modem} modemType={modem.modemType} />
                    <Typography paragraph><b>Remote Connectivity Enabled:</b> {modem.remoteConnectivityEnabled ? 'Enabled' : 'Disabled'}</Typography>
                </Grid>
                <Grid container item sm={2} direction='column' spacing={2} alignItems='center'>
                    <Grid item>
                        {modem && (modem.ipAddress) &&
                            <ConnectionButton
                                large={true} localIp={modemIpSetting?.[0]} connected={!!modemIpSetting}
                                onConnect={() => {
                                    (modem.ipAddress) && props.addOrUpdateIpSettings(
                                        [[undefined, { type: 'Modem', remoteIp: (modem.ipAddress)!, modemHostId: modem.modemHostId }]]
                                    );
                                }}
                                openOnConnect={true}
                                showDisconnect={false}
                                connectLabel="Manage Modem"
                            />
                        }
                    </Grid>
                    <Grid item>
                        <DebounceButton delay={10000} disabled={disableConnect} buttonAction={ButtonAction.PowerCycle} modemId={modem.modemHostId} />
                    </Grid>
                </Grid>
            </Grid>
        )
    }

    async function onFormSubmit(values: ModemFormProperties, modem: ModemWithOrgSiteResponseDTO) {
        try {
            if (values.label !== modem.label || values.notes !== modem.notes || values.remoteConnectivityEnabled !== modem.remoteConnectivityEnabled)
                props.updateModemProperties(modem.modemHostId,
                    { label: values.label, remoteConnectivityEnabled: values.remoteConnectivityEnabled, notes: values.notes ?? undefined })
            // need to remove the current site before changing organizations, otherwise we'll get an error
            if (currentSite && currentSite.externalId !== formSite?.externalId) {
                await new SitesClient().removeModem2(currentSite.externalId, modem.modemHostId);
            }
            if (currentOrganization?.externalId !== formOrganization?.externalId) {
                if (currentOrganization) {
                    await new OrganizationsClient().removeModem(currentOrganization.externalId, modem.modemHostId);
                }
                if (formOrganization) {
                    await new OrganizationsClient().addModem(formOrganization.externalId, modem.modemHostId);
                }
                setCurrentOrg(formOrganization)
            }
            if (currentSite?.externalId !== formSite?.externalId) {
                if (formSite)
                    await new SitesClient().addModem2(formSite.externalId, modem.modemHostId);
                setCurrentSite(formSite)
            }
            dispatch(snackbar.addMessage({ title: "Success", message: "Successfully updated modem", alertType: 'success' }))
        }
        catch (error) {
            dispatch(snackbar.addError(error, undefined, 'Error changing organization or site for modem'));
        }
    }

    function dropDownSitesForOrg(orgId: string) {
        const dropDownSites = allSites.filter(s => s.organizationId === orgId);
        setDropDownSites(dropDownSites);
    }

    function renderConfigureModem(modem: ModemWithOrgSiteResponseDTO, twin: ReportedTwinDTO | undefined) {
        return (
            <Formik initialValues={{ label: modem.label, remoteConnectivityEnabled: modem.remoteConnectivityEnabled, notes: modem.notes ?? "" }}
                validationSchema={ModemFormSchema}
                onSubmit={async (values, _) => onFormSubmit(values as ModemFormProperties, modem)}>
                {/* supply a function to formik so that we can access these props*/}
                {({ submitForm, isSubmitting, touched }) => (
                    <Grid container>
                        <Grid item sm={10}>
                            <Form translate='yes'>
                                <Typography paragraph><b>Modem Host ID:</b> {modem.modemHostId}</Typography>
                                <Typography paragraph><b>Serial ID:</b> {twin?.serialNumber ?? ''}</Typography>
                                <Typography paragraph><b>Created Date:</b> {utcToLocal(modem.created).toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS)}</Typography>
                                <Typography paragraph><b>Last Updated:</b> {utcToLocal(modem.lastUpdated).toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS)}</Typography>
                                <Grid container alignItems='center' spacing={1}>
                                    <Grid item>
                                        <Typography paragraph display='inline'><b>Label:</b></Typography>
                                    </Grid>
                                    <Grid item>
                                        {canEditModem ?
                                            <Field component={TextField} name='label' type='text' />
                                            : <Typography>{modem.label}</Typography>
                                        }
                                    </Grid>
                                </Grid>
                                <Grid container alignItems='center' spacing={1}>
                                    <Grid item>
                                        <Typography paragraph display='inline'><b>Organization:</b></Typography>
                                    </Grid>
                                    <Grid item>
                                        {allOrganizations.length > 0 && // to avoid a warning
                                            <Field
                                                component={Select}
                                                name={FormOrganizationId}
                                                onChange={(event: any) => changeHandler(event, modem)}
                                                value={formOrganization?.externalId || ''}
                                                disabled={!isGlobalUser}
                                            >
                                                {allOrganizations.map((org) => <MenuItem value={org.externalId} key={org.externalId}>{org.name}</MenuItem>)}
                                            </Field>}
                                    </Grid>
                                </Grid>
                                <Grid container alignItems='center' spacing={1}>
                                    <Grid item>
                                        <Typography paragraph display='inline'><b>Site:</b></Typography>
                                    </Grid>
                                    <Grid item>
                                        {allSites.length > 0 &&
                                            <Field
                                                component={Select}
                                                name={FormSiteId}
                                                onChange={(event: any) => changeHandler(event, modem)}
                                                value={formSite?.externalId || ''}
                                                disabled={!canEditModem}
                                            >
                                                {dropDownSites.map((site) => <MenuItem value={site.externalId} key={site.externalId}>{site.name}</MenuItem>)}
                                            </Field>}
                                    </Grid>
                                </Grid>
                                <Typography><b>Remote Connectivity Enabled:</b> <Field disabled={!isGlobalUser} component={Checkbox} type='checkbox' name='remoteConnectivityEnabled' /></Typography>
                                <Grid container spacing={1}>
                                    <Grid item>
                                        <Typography paragraph display='inline'><b>Notes:</b></Typography>
                                    </Grid>
                                    <Grid item>
                                        <Field component={MultiLineTextField} name='notes' type='text' />
                                    </Grid>
                                </Grid>
                            </Form>
                            {isGlobalAdmin && <ModemPassword modemId={modem.modemHostId}></ModemPassword>}
                        </Grid>
                        {canEditModem &&
                            <Grid container item sm={2} direction='column' spacing={2} alignItems='center'>
                                <Grid item>
                                    <Button variant='contained' color='primary' size='medium' disabled={isSubmitting} onClick={submitForm}>Save Changes</Button>
                                </Grid>
                                <Grid item>
                                    <Button component={RouterLink} variant='contained' color='primary' size='medium' to={formOrganization ? `/organizations/${formOrganization.externalId}/addSite` : `/sites/create`}>Create New Site</Button>
                                </Grid>
                            </Grid>}
                    </Grid>
                )}
            </Formik>
        )
    }

    function renderTabs(modem: ModemWithOrgSiteResponseDTO, twin: ReportedTwinDTO | undefined) {
        return (
            <>
                <Typography variant='h4' id="tabelLabel">{(modem.label == null || modem.label == "") ? modem.modemHostId : modem.label}</Typography>
                <Paper square>
                    <Tabs
                        value={tabValue}
                        onChange={tabHandler}
                        variant="scrollable"
                        scrollButtons="auto"
                    >
                        <Tab label="About" value={AboutTabValue} />
                        <Tab label="Configuration" value={ConfigurationTabValue} />
                        <Tab label="Connected Network Devices" value={ConnectedDevicesTabValue} />
                        {/* <Tab label="Subgroups"value={SubgroupsTabValue} /> */}

                    </Tabs>
                </Paper>
                <TabPanel value={tabValue} index={ConfigurationTabValue}>{renderConfigureModem(modem, twin)}</TabPanel>
                <TabPanel value={tabValue} index={ConnectedDevicesTabValue}>
                    {twin ?
                        (twin.gateways.length > 0 ? renderGateways(props.match.params.modemId, twin) : <Typography>No network devices are connected</Typography>)
                        : <Typography>No Twin Data could be loaded. Ensure the modem is on and connected over cellular or to the Internet</Typography>}
                </TabPanel>
                {/* <TabPanel value={this.state.tabValue} index={SubgroupsTabValue}>Subgroups Info - TODO</TabPanel> */}
                <TabPanel value={tabValue} index={AboutTabValue}> {renderAboutModem(modem, twin, heartBeatValue)} </TabPanel>
            </>
        )
    }


    function renderGateways(modemId: string, twin: ReportedTwinDTO) {
        return (
            <TableContainer component={Paper} aria-labelledby="connectedEclypses">
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Label</TableCell>
                            <TableCell>nLight Id</TableCell>
                            <TableCell>Remote IP</TableCell>
                            <TableCell>Remote Port</TableCell>
                            <TableCell>Firmware Version</TableCell>
                            <TableCell>Connect</TableCell>
                            <TableCell>Manage</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {twin.gateways.map(g => renderGateway(g, modemId))}
                    </TableBody>
                </Table>
            </TableContainer>)
    }

    function renderGateway(gateway: ReportedTwinGatewayDTO, modemHostId: string) {
        const eclypseIpSetting = Object.entries(props.transportConfig?.simulatorToRemoteIpMap ?? {})
            .find(([ip, s]) => s.type === 'Eclypse' && s.nLightId === gateway.deviceId)
        return (
            <TableRow key={gateway.deviceId}>
                <TableCell>{gateway.label}</TableCell>
                <TableCell>{gateway.deviceId}</TableCell>
                <TableCell>{gateway.ipAddress}</TableCell>
                <TableCell>{gateway.webServerPort}</TableCell>
                <TableCell>{gateway.firmware}</TableCell>
                <TableCell>
                    <ConnectionButton disabled={disableConnect} connected={!!eclypseIpSetting} localIp={eclypseIpSetting?.[0]}
                        onConnect={() => props.addOrUpdateIpSettings(
                            [[undefined, { type: 'Eclypse', remoteIp: gateway.ipAddress, modemHostId: modemHostId, nLightId: gateway.deviceId }]])}
                        onDisconnect={() => props.removeIpSettings([eclypseIpSetting!])} /></TableCell>
                <TableCell>{eclypseIpSetting ? <Link target="_blank" href={`https://${eclypseIpSetting[0]}`}>Go to Eclypse page</Link> : ' '}</TableCell>
            </TableRow>
        )
    }

    // Get Modem and Twin for ModemId passed in params
    useEffect(() => {
        props.requestModem(props.match.params.modemId);
        props.requestTwin(props.match.params.modemId, DeviceTwinOrHeartbeatRequestedType._0);
        return () => {
            props.cancelTwinRequest(props.match.params.modemId)
        };
    }, [props.match.params.modemId])

    // Get Org data for modem
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [orgs, sites] = await Promise.all([new OrganizationsClient().getMany(), new SitesClient().getMany2()])
                setAllOrganizations(orgs)
                setAllSites(sites)
            }
            catch {
                setAllOrganizations([]);
                setAllSites([]);
            }
        }
        fetchData();
    }, [props.match.params.modemId])

    // Set Sites drop down
    useEffect(() => {
        if (allOrganizations.length > 0 && allSites.length > 0 && modem) {
            const modemOrg = allOrganizations.find(s => s.externalId === modem.organizationId)
            if (modemOrg) {
                dropDownSitesForOrg(modemOrg.externalId);
                setCurrentOrg(modemOrg)
                setFormOrg(modemOrg);
            }
            const currentSite = allSites.find(s => s.externalId === modem.siteId);
            if (currentSite) {
                setCurrentSite(currentSite)
                setFormSite(currentSite);
            }
        }
    }, [modem, allOrganizations, allSites])

    // Get Hearbeat 
    useEffect(() => {
        const fetchData = async () => {
            try {
                if (modem) {
                    const hbeat = await new ModemsClient().getHeartbeat(modem.modemHostId, DeviceTwinOrHeartbeatRequestedType.Simple);
                    setHearbeat(hbeat.heartbeat?.Fields as HeartbeatDTO | undefined);
                }
            }
            catch {
                setHearbeat(undefined);
            }
        }
        fetchData();
    }, [setHearbeat, modem])

    return modem ? renderTabs(modem, simpleTwin) : <Typography>Loading...</Typography>
}

export default connect(
    // extract the state we need. Note that just because our typings above include it, we still need this data to match those typings, the compiler won't check this for us
    (state: ApplicationState) => ({ ...state.transportAppState, ...state.modems }),
    // likewise extract the action creators we need
    { ...TransportStore.actionCreators, ...ModemsStore.actionCreators }
)(ModemDetail as any)